import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { FiDownload, FiEdit2, FiEye } from "react-icons/fi";
import API_URL from "../Config";
import "../forms/UserForm.css";
import "./miIndex.css";
import {
  MI_AFS_CUSTOMER_OPTIONS,
  MI_AFS_SALES_VOLUME_OPTIONS,
  MI_DEPOT_CAPACITY_OPTIONS,
  MI_DEPOT_OPERATION_OPTIONS,
  calculateComplexityPreview,
  downloadMiAttachment,
  formatMiDate,
  formatMiDateTime,
  formatMiNumber,
  formatMiPercent,
  getComplexityStateFromApi,
  getMiAttachmentName,
  getMiAttachmentSize,
  getMiScoreValue,
  openMiAttachment,
  parseMiApiError,
} from "./miIndexUtils";

const getOptionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || value || "NA";

function ViewMIEvaluation({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [evaluationData, setEvaluationData] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const loadEvaluation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mi/evaluations/${id}`, {
        headers: {
          Authorization: localStorage.getItem("authKey") || "",
        },
      });

      const payload = response.data || {};
      setEvaluationData(payload);

      const expanded = {};
      (payload.categories || []).forEach((category, index) => {
        expanded[category.category_id] = index === 0;
      });
      setExpandedCategories(expanded);
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to load evaluation details"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [id, showSnackbar]);

  useEffect(() => {
    loadEvaluation();
  }, [loadEvaluation]);

  const evaluation = evaluationData?.evaluation || null;
  const categories = useMemo(
    () => evaluationData?.categories || [],
    [evaluationData],
  );
  const negativeMarking = evaluationData?.negative_marking || null;
  const complexityFactor = evaluationData?.complexity_factor || null;

  const totalItems = useMemo(
    () =>
      categories.reduce(
        (count, category) => count + (category.items?.length || 0),
        0,
      ),
    [categories],
  );

  const totalAttachments = useMemo(
    () =>
      categories.reduce(
        (count, category) =>
          count +
          (category.items || []).reduce(
            (itemCount, item) => itemCount + (item.attachments?.length || 0),
            0,
          ),
        0,
      ),
    [categories],
  );

  const complexityDetails = useMemo(() => {
    if (!evaluation || !complexityFactor) return null;
    const draft = getComplexityStateFromApi(
      evaluation.location_type,
      complexityFactor,
    );
    const preview = calculateComplexityPreview(evaluation.location_type, draft);
    return { draft, preview };
  }, [complexityFactor, evaluation]);

  const handleToggleCategory = (categoryId) => {
    setExpandedCategories((prevState) => ({
      ...prevState,
      [categoryId]: !prevState[categoryId],
    }));
  };

  const handleViewAttachment = async (attachmentId) => {
    try {
      await openMiAttachment(attachmentId);
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to open attachment"),
        "error",
      );
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      await downloadMiAttachment(attachmentId, filename);
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to download attachment"),
        "error",
      );
    }
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading MI evaluation...</div>
      </div>
    );
  }

  if (!evaluation) {
    return (
      <>
        <div className="error-container">
          <div>Evaluation not found.</div>
        </div>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </>
    );
  }

  return (
    <>
      <div
        className={`data-edit-form-content-container page-content mi-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-edit-form-content mi-page-layout">
          <div className="data-view-header">
            {/* <div className="data-view-header-content"> */}
            <div className="data-view-heading-content-left-side mi-standard-header__title">
              <Tooltip title="Back" arrow>
                <IoMdArrowBack
                  className="back-company"
                  onClick={() => navigate("/mi-evaluations")}
                />
              </Tooltip>
              <span>MI Evaluation Summary</span>
            </div>

            <div className="data-view-heading-content-right-side mi-standard-header__actions">
              {/* {evaluation.evaluation_id ? (
                  <span className="mi-id-badge">
                    ID: {evaluation.evaluation_id}
                  </span>
                ) : null} */}
              <span className={`mi-pill mi-pill--${evaluation.status}`}>
                {evaluation.status}
              </span>
              <Link to={`/mi-edit-evaluation/${evaluation.evaluation_id}`}>
                <button className="mi-btn mi-btn--secondary" type="button">
                  <FiEdit2 /> Open Workflow
                </button>
              </Link>
            </div>
            {/* </div> */}

            {/* <div className="mi-standard-header__subtitle">
              Review final scoring, negative marking, complexity factor, and
              attachments in one place.
            </div> */}
          </div>
          <div className="header-separator"></div>

          <div className="data-view-body">
            <div className="mi-shell">
              <div className="mi-stats" style={{padding:"0px"}}>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Final MI Score</div>
                  <div className="mi-stat__value">
                    {formatMiNumber(evaluation.final_mi_score || 0)}
                  </div>
                  <div className="mi-stat__meta">Calculated final score</div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Score Percentage</div>
                  <div className="mi-stat__value">
                    {formatMiPercent(evaluation.percentage_obtained || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    {formatMiNumber(evaluation.total_marks_obtained || 0)} /{" "}
                    {formatMiNumber(evaluation.total_max_marks || 97)}
                  </div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Scored Items</div>
                  <div className="mi-stat__value">{totalItems}</div>
                  <div className="mi-stat__meta">
                    Items included in this evaluation
                  </div>
                </div>
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Attachments</div>
                  <div className="mi-stat__value">{totalAttachments}</div>
                  <div className="mi-stat__meta">
                    Documents linked to scored items
                  </div>
                </div>
              </div>

              <div className="mi-section mi-card" style={{margin:"0px"}}>
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Evaluation Details</h2>
                    {/* <p className="mi-section__subtitle">
                      Header information from the evaluation master record.
                    </p> */}
                  </div>
                </div>

                <div className="mi-readonly-grid">
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Location</div>
                    <div className="mi-readonly__value">
                      {evaluation.location_name || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Location Type</div>
                    <div className="mi-readonly__value">
                      {evaluation.location_type || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Region</div>
                    <div className="mi-readonly__value">
                      {evaluation.region || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Evaluation Date</div>
                    <div className="mi-readonly__value">
                      {formatMiDate(evaluation.evaluation_date)}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Evaluator Name</div>
                    <div className="mi-readonly__value">
                      {evaluation.evaluator_name || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">
                      Evaluator Designation
                    </div>
                    <div className="mi-readonly__value">
                      {evaluation.evaluator_designation || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Evaluation Type</div>
                    <div className="mi-readonly__value">
                      {evaluation.evaluation_type || "NA"}
                    </div>
                  </div>
                  <div className="mi-readonly">
                    <div className="mi-readonly__label">Last Updated</div>
                    <div className="mi-readonly__value">
                      {formatMiDateTime(evaluation.updated_at)}
                    </div>
                  </div>
                  <div className="mi-readonly" style={{ gridColumn: "1 / -1" }}>
                    <div className="mi-readonly__label">Remarks</div>
                    <div className="mi-readonly__value">
                      {evaluation.remarks || "NA"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mi-section mi-card" style={{margin:"0px"}}>
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Category-Wise Scoring</h2>
                    <p className="mi-section__subtitle">
                      Each saved score, observation, and attachment grouped by
                      category.
                    </p>
                  </div>
                </div>

                {categories.length === 0 ? (
                  <div className="mi-empty">
                    No scoring records are available yet.
                  </div>
                ) : (
                  <div className="mi-accordion">
                    {categories.map((category) => (
                      <div
                        className="mi-accordion__item"
                        key={category.category_id}
                      >
                        <button
                          className="mi-accordion__header"
                          type="button"
                          onClick={() =>
                            handleToggleCategory(category.category_id)
                          }
                        >
                          <div className="mi-accordion__title">
                            <span className="mi-accordion__code">
                              {category.category_code}
                            </span>
                            <span className="mi-accordion__name">
                              {category.category_name}
                            </span>
                            <span className="mi-pill mi-pill--score">
                              {(category.items || []).length} items
                            </span>
                          </div>
                          <div>
                            {expandedCategories[category.category_id] ? (
                              <MdExpandLess size={20} />
                            ) : (
                              <MdExpandMore size={20} />
                            )}
                          </div>
                        </button>

                        {expandedCategories[category.category_id] && (
                          <div className="mi-accordion__body">
                            <div className="mi-items">
                              {(category.items || []).map((item) => {
                                const scoreValue = getMiScoreValue(item);
                                return (
                                  <div className="mi-item" key={item.item_id}>
                                    <div className="mi-item__header">
                                      <div>
                                        <span className="mi-item__code">
                                          {item.item_code}
                                        </span>
                                        <span className="mi-item__description">
                                          {item.item_description}
                                        </span>
                                      </div>
                                      <div className="mi-pill mi-pill--score">
                                        {scoreValue === null
                                          ? "NA"
                                          : formatMiNumber(scoreValue)}{" "}
                                        / {formatMiNumber(item.max_marks || 0)}
                                      </div>
                                    </div>

                                    <div className="mi-item__body">
                                      {item.subcategory_scores &&
                                      Object.keys(item.subcategory_scores)
                                        .length > 0 ? (
                                        <div className="mi-table-wrap">
                                          <table className="mi-table">
                                            <thead>
                                              <tr>
                                                <th>Subcategory</th>
                                                <th>Score</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {Object.entries(
                                                item.subcategory_scores,
                                              ).map(([key, value]) => (
                                                <tr key={key}>
                                                  <td>{key}</td>
                                                  <td>
                                                    {formatMiNumber(value || 0)}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : null}

                                      <div className="mi-grid-3">
                                        <div className="mi-readonly">
                                          <div className="mi-readonly__label">
                                            Allotted Mark
                                          </div>
                                          <div className="mi-readonly__value">
                                            {item.allotted_mark === null ||
                                            item.allotted_mark === undefined
                                              ? "NA"
                                              : formatMiNumber(
                                                  item.allotted_mark,
                                                )}
                                          </div>
                                        </div>
                                        <div className="mi-readonly">
                                          <div className="mi-readonly__label">
                                            Average Score
                                          </div>
                                          <div className="mi-readonly__value">
                                            {item.average_score === null ||
                                            item.average_score === undefined
                                              ? "NA"
                                              : formatMiNumber(
                                                  item.average_score,
                                                )}
                                          </div>
                                        </div>
                                        <div className="mi-readonly">
                                          <div className="mi-readonly__label">
                                            Weighted Score
                                          </div>
                                          <div className="mi-readonly__value">
                                            {item.weighted_score === null ||
                                            item.weighted_score === undefined
                                              ? "NA"
                                              : formatMiNumber(
                                                  item.weighted_score,
                                                )}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="mi-readonly">
                                        <div className="mi-readonly__label">
                                          Observations
                                        </div>
                                        <div className="mi-readonly__value">
                                          {item.observations || "NA"}
                                        </div>
                                      </div>

                                      <div className="mi-attachments">
                                        <div className="mi-readonly__label">
                                          Attachments
                                        </div>
                                        {(item.attachments || []).length > 0 ? (
                                          (item.attachments || []).map(
                                            (attachment) => (
                                              <div
                                                className="mi-attachment"
                                                key={attachment.id}
                                              >
                                                <div className="mi-attachment__meta">
                                                  <div className="mi-attachment__name">
                                                    {getMiAttachmentName(
                                                      attachment,
                                                    )}
                                                  </div>
                                                  <div className="mi-attachment__sub">
                                                    {getMiAttachmentSize(
                                                      attachment,
                                                    )}{" "}
                                                    | Uploaded{" "}
                                                    {formatMiDateTime(
                                                      attachment.uploaded_at,
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="mi-attachment__actions">
                                                  <button
                                                    className="mi-btn mi-btn--secondary"
                                                    type="button"
                                                    onClick={() =>
                                                      handleViewAttachment(
                                                        attachment.id,
                                                      )
                                                    }
                                                  >
                                                    <FiEye /> View
                                                  </button>
                                                  <button
                                                    className="mi-btn mi-btn--secondary"
                                                    type="button"
                                                    onClick={() =>
                                                      handleDownloadAttachment(
                                                        attachment.id,
                                                        getMiAttachmentName(
                                                          attachment,
                                                        ),
                                                      )
                                                    }
                                                  >
                                                    <FiDownload /> Download
                                                  </button>
                                                </div>
                                              </div>
                                            ),
                                          )
                                        ) : (
                                          <div className="mi-empty">
                                            No attachments added.
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mi-section mi-card" style={{margin:"0px"}}>
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Negative Marking</h2>
                    {/* <p className="mi-section__subtitle">
                      Summary returned by the negative-marking API.
                    </p> */}
                  </div>
                </div>

                {negativeMarking ? (
                  <div className="mi-report-stats">
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">
                        Major Recommendation Date
                      </div>
                      <div className="mi-stat__value" style={{ fontSize: 18 }}>
                        {formatMiDate(
                          negativeMarking.major_recommendations_date,
                        )}
                      </div>
                      <div className="mi-stat__meta">
                        Reference date for compliance
                      </div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">Pending 1-2 Years</div>
                      <div className="mi-stat__value">
                        {negativeMarking.non_compliance_1to2_years || 0}
                      </div>
                      <div className="mi-stat__meta">
                        Mark:{" "}
                        {formatMiNumber(negativeMarking.marks_1to2_years || 0)}
                      </div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">Pending &gt;2 Years</div>
                      <div className="mi-stat__value">
                        {negativeMarking.non_compliance_gt2_years || 0}
                      </div>
                      <div className="mi-stat__meta">
                        Mark:{" "}
                        {formatMiNumber(negativeMarking.marks_gt2_years || 0)}
                      </div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">Applied Deduction</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(
                          negativeMarking.actual_negative_marks_deducted ||
                            negativeMarking.total_negative_marks ||
                            0,
                        )}
                      </div>
                      <div className="mi-stat__meta">
                        NC%:{" "}
                        {formatMiPercent(
                          negativeMarking.percentage_non_compliance || 0,
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mi-empty">
                    Negative marking has not been submitted yet.
                  </div>
                )}

                {negativeMarking?.remarks ? (
                  <>
                    <div className="mi-divider"></div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Remarks</div>
                      <div className="mi-readonly__value">
                        {negativeMarking.remarks}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="mi-section mi-card" style={{ margin: "0px" }}>
                <div className="mi-section__header">
                  <div>
                    <h2 className="mi-section__title">Complexity Factor</h2>
                    <p className="mi-section__subtitle">
                      Derived from the saved calculation details for this
                      location type.
                    </p>
                  </div>
                </div>

                {complexityFactor && complexityDetails ? (
                  <>
                    <div className="mi-report-stats">
                      <div className="mi-card mi-stat">
                        <div className="mi-stat__label">W Weight</div>
                        <div className="mi-stat__value">
                          {formatMiNumber(complexityDetails.preview.W)}
                        </div>
                        <div className="mi-stat__meta">
                          {evaluation.location_type === "AFS"
                            ? "Sales volume"
                            : "Capacity"}
                        </div>
                      </div>
                      <div className="mi-card mi-stat">
                        <div className="mi-stat__label">P Weight</div>
                        <div className="mi-stat__value">
                          {formatMiNumber(complexityDetails.preview.P)}
                        </div>
                        <div className="mi-stat__meta">Zone factor</div>
                      </div>
                      <div className="mi-card mi-stat">
                        <div className="mi-stat__label">A Weight</div>
                        <div className="mi-stat__value">
                          {formatMiNumber(complexityDetails.preview.A)}
                        </div>
                        <div className="mi-stat__meta">Age factor</div>
                      </div>
                      <div className="mi-card mi-stat">
                        <div className="mi-stat__label">Total Complexity</div>
                        <div className="mi-stat__value">
                          {formatMiNumber(
                            complexityFactor.total_complexity_factor ||
                              complexityDetails.preview.total,
                            4,
                          )}
                        </div>
                        <div className="mi-stat__meta">
                          Saved complexity factor
                        </div>
                      </div>
                    </div>

                    <div className="mi-divider"></div>

                    <div className="mi-readonly-grid">
                      {evaluation.location_type === "AFS" ? (
                        <div className="mi-readonly">
                          <div className="mi-readonly__label">Sales Volume</div>
                          <div className="mi-readonly__value">
                            {getOptionLabel(
                              MI_AFS_SALES_VOLUME_OPTIONS,
                              complexityDetails.draft.sales_volume,
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="mi-readonly">
                          <div className="mi-readonly__label">Capacity</div>
                          <div className="mi-readonly__value">
                            {getOptionLabel(
                              MI_DEPOT_CAPACITY_OPTIONS,
                              complexityDetails.draft.capacity,
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mi-readonly">
                        <div className="mi-readonly__label">Zone</div>
                        <div className="mi-readonly__value">
                          {complexityDetails.draft.zone || "NA"}
                        </div>
                      </div>

                      <div className="mi-readonly">
                        <div className="mi-readonly__label">
                          Installation Age
                        </div>
                        <div className="mi-readonly__value">
                          {complexityDetails.draft.age_over_25
                            ? "Over 25 years"
                            : "25 years or less"}
                        </div>
                      </div>

                      <div className="mi-readonly">
                        <div className="mi-readonly__label">
                          {evaluation.location_type === "AFS"
                            ? "Customers"
                            : "Operations"}
                        </div>
                        <div className="mi-readonly__value">
                          {(evaluation.location_type === "AFS"
                            ? complexityDetails.draft.customers.map((value) =>
                                getOptionLabel(MI_AFS_CUSTOMER_OPTIONS, value),
                              )
                            : complexityDetails.draft.operations.map((value) =>
                                getOptionLabel(
                                  MI_DEPOT_OPERATION_OPTIONS,
                                  value,
                                ),
                              )
                          ).join(", ") || "NA"}
                        </div>
                      </div>

                      <div
                        className="mi-readonly"
                        style={{ gridColumn: "1 / -1" }}
                      >
                        <div className="mi-readonly__label">Remarks</div>
                        <div className="mi-readonly__value">
                          {complexityFactor.remarks || "NA"}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mi-empty">
                    Complexity factor has not been submitted yet.
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* <div className="footer">
            <div className="button-group">
              <button
                className="data-edit-form-cancel-button"
                type="button"
                onClick={() => navigate("/mi-evaluations")}
              >
                Back To List
              </button>
            </div>
          </div> */}
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ViewMIEvaluation;
